import React, { useState } from 'react';
import { Button, Input, Label, Textarea, Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter } from '@clinic-os/ui';
import type { Patient } from '@clinic-os/shared/types';
import { DEMO_CLINIC_ID } from '../../data/demo-data';

interface AddPatientDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

export function AddPatientDialog({ open, onClose, onAdd }: AddPatientDialogProps) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    allergies: '',
    medications: '',
    notes: '',
    clinicId: DEMO_CLINIC_ID,
  });

  const handleSubmit = () => {
    if (!form.firstName || !form.lastName) return;
    onAdd({
      clinicId: form.clinicId,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email || undefined,
      phone: form.phone || undefined,
      dateOfBirth: form.dateOfBirth || undefined,
      allergies: form.allergies ? form.allergies.split(',').map((s) => s.trim()) : [],
      medications: form.medications ? form.medications.split(',').map((s) => s.trim()) : [],
      notes: form.notes || undefined,
    });
    setForm({ firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '', allergies: '', medications: '', notes: '', clinicId: DEMO_CLINIC_ID });
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-md">
      <DialogHeader>
        <DialogTitle>Add New Patient</DialogTitle>
      </DialogHeader>
      <DialogContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              placeholder="Maria"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              placeholder="Garcia"
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="patient@email.com"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+66 81 234 5678"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dob">Date of Birth</Label>
            <Input
              id="dob"
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="allergies">Allergies (comma-separated)</Label>
          <Input
            id="allergies"
            value={form.allergies}
            onChange={(e) => setForm((f) => ({ ...f, allergies: e.target.value }))}
            placeholder="Penicillin, Codeine"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="medications">Current Medications (comma-separated)</Label>
          <Input
            id="medications"
            value={form.medications}
            onChange={(e) => setForm((f) => ({ ...f, medications: e.target.value }))}
            placeholder="Metformin 500mg"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Additional notes about the patient..."
            className="min-h-[60px]"
          />
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!form.firstName || !form.lastName}>
          Add Patient
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
